/* =====================================================================
   Alex & Lace — heading reveal (Pass 2), SHARED mechanism — GSAP.

   Used by BOTH the homepage hero headline (plays on LOAD) and the collection
   page title (plays on SCROLL into view). One motion language site-wide.

   Smoothness (see al-word-reveal.css for the pre-hide/reserve rationale):
   - NO FLASH — the heading is pre-hidden before first paint (.al-reveal-armed
     on <html>, set by an inline head script); this JS builds the split + sets
     initial states, then un-hides, so un-split text never flashes.
   - NO MID-CASCADE REFLOW — the timeline is gated on document.fonts.ready
     (with a fallback), so the self-hosted Nohemi swap can't reflow mid-cascade.
   - COMPOSITOR-ONLY — the cascade rises (yPercent) + fades; no animated filters.
   - LAYOUT RESERVED — the split never changes the heading's box (fixed line
     metrics), so document flow doesn't shift.

   Invariants: a11y two-layer (clean .visually-hidden heading + aria-hidden
   split), collection <sup> stays OUTSIDE the splitter, reduced-motion → final
   state (no animation).

   REVEAL (LOCKED, defaults-lock 2026-07-17): the per-LETTER escape.cafe-style
   ordered cascade — rise + heavy overlap → one wave. The preview treatments
   (mask / lift / focus, per-word) were deleted at defaults-lock.
   ===================================================================== */

(() => {
  const gsap = window.gsap;

  // LOCKED (defaults-lock 2026-07-17): the ordered per-LETTER cascade, everywhere (hero on load,
  // collection titles on scroll, incl. short ones). The mask/lift/focus preview treatments were
  // deleted; this is the single shipped reveal.
  const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = () => matchMedia('(max-width: 1023px)').matches;

  const unarm = () => document.documentElement.classList.remove('al-reveal-armed');

  // Build the split + initial hidden states. Returns a job to play later, or null
  // (reduced motion / no GSAP → final state, nothing to play).
  function build(root, textEl) {
    if (!root || !textEl || root.dataset.alWordRevealReady === 'true') return null;

    const clean = textEl.textContent.replace(/\s+/g, ' ').trim();
    if (!clean) return null;
    const words = clean.split(' ');

    // Layer 1 (assistive tech): one clean, unfragmented copy.
    const srCopy = document.createElement('span');
    srCopy.className = 'visually-hidden';
    srCopy.textContent = clean;

    // Layer 2 (visible): split, hidden from AT. Words wrap; the moving parts are per-LETTER .__char.
    // No clip wrapper: glyphs are NEVER clipped (Nohemi 900's ascenders/descenders/overshoot are
    // large; a tight clip shaved them mid-animation). The reveal is a rise+fade — displaced glyphs
    // are near-transparent, so nothing overlaps or gets shaved.
    const wrap = document.createElement('span');
    wrap.className = 'al-word-reveal';
    wrap.setAttribute('aria-hidden', 'true');

    const targets = [];
    const wordTargets = [];
    words.forEach((word, i) => {
      const w = document.createElement('span');
      w.className = 'al-word-reveal__word';
      wordTargets.push(w);
      Array.from(word).forEach((ch) => {
        const s = document.createElement('span');
        s.className = 'al-word-reveal__char';
        s.textContent = ch;
        w.appendChild(s);
        targets.push(s);
      });
      wrap.appendChild(w);
      if (i < words.length - 1) wrap.appendChild(document.createTextNode(' '));
    });

    textEl.replaceChildren(srCopy, wrap);
    root.dataset.alWordRevealReady = 'true';

    // Fallback: no GSAP or reduced motion → final visible state, no animation.
    if (!gsap || reduceMotion()) {
      textEl.style.visibility = 'visible';
      wrap.classList.add('is-revealed');
      return null;
    }

    // GRANULARITY (additive 2026-07-30, About story-scroll). Opt-in per element via
    // data-al-reveal-granularity="word": animate the .__word wrappers instead of the .__char spans.
    // DEFAULT IS UNCHANGED — absent the attribute this is per-LETTER exactly as locked 2026-07-17, so
    // the homepage hero headline and every collection title keep the identical cascade.
    //
    // WHY it exists: per-letter is a HEADLINE mechanism. On a 96-character sentence it is
    // 96 × 0.032 = 3.07s of stagger before the last glyph even starts — the wave stops reading as one
    // gesture and turns into a typewriter. Per-word on the same sentence is 16 × 0.05 = 0.8s. Same
    // rise+fade, same power3.out, same ordered from:'start' — only the unit of the wave changes, so it
    // is still one motion language and NOT a second reveal treatment.
    //
    // The .__word spans already exist (they are what makes multi-word text wrap normally), so this
    // needs no extra DOM — it only re-points the animation at a coarser layer of the same split.
    const perWord = root.dataset.alRevealGranularity === 'word';
    const animTargets = perWord ? wordTargets : targets;

    // Initial hidden state: a moderate rise (no clip) — displaced far enough to read as a rise,
    // small enough that the near-transparent glyph never reaches the neighbouring line.
    gsap.set(animTargets, { yPercent: 55, opacity: 0 });
    // Un-hide the element: its content stays invisible via the initial state above,
    // so this cannot flash un-split or final-state text.
    textEl.style.visibility = 'visible';

    // onScroll = fire when the heading scrolls into view (vs. the hero's on-load cascade). True for the
    // collection title AND the NEUTRAL, intent-named scroll hook (data-al-reveal-scroll) added additively
    // 2026-07-24 for the About page — one scroll-reveal mechanism, page-agnostic. Both stay supported.
    return { targets: animTargets, perWord, mobile: isMobile(), onScroll: root.matches('[data-al-collection-title], [data-al-reveal-scroll]'), trigger: root, wrap };
  }

  // The reveal tween vars for a job (no scrollTrigger — the caller attaches one). Ordered cascade:
  // rise + fade with heavy overlap → reads as one wave. Per-LETTER by default; per-WORD when the
  // element opted in (see build()). Only the stagger step differs — the rise, the fade, the duration
  // and the ease are shared, which is what keeps both granularities one language.
  function revealVars(job) {
    const { mobile, perWord } = job;
    const d = mobile ? 0.62 : 1;
    // Per-word steps LARGER than per-letter (0.05 vs 0.032): there are ~6× fewer targets, so the same
    // per-target step would collapse the wave into a single simultaneous pop. 16 words × 0.05 = 0.8s
    // of stagger + 0.9s duration ≈ 1.7s total, which is the same order as a headline cascade.
    const step = perWord ? (mobile ? 0.03 : 0.05) : (mobile ? 0.018 : 0.032);
    return {
      yPercent: 0, opacity: 1,
      duration: 0.9 * d, ease: 'power3.out',
      stagger: { each: step, from: 'start' },
    };
  }

  function playImmediate(job) {
    gsap.to(job.targets, revealVars(job));
  }

  function playOnScroll(job) {
    const ST = window.ScrollTrigger;
    if (!ST) return playImmediate(job); // no ScrollTrigger → don't leave it hidden
    // Fires when the title enters (~top 80%); if already in view on load, GSAP plays it
    // immediately on the ScrollTrigger's first evaluation. once: true = play a single time.
    gsap.to(job.targets, {
      ...revealVars(job),
      scrollTrigger: { trigger: job.trigger, start: 'top 80%', once: true },
    });
  }

  // --- Load-time gates for the HERO cascade (collection is scroll-fired, so it never
  //     competes with load contention). The hero cascade must NOT start in the same frame
  //     as hero-image decode / ScrollTrigger init / first paint, or it drops frames on a
  //     cold, CPU-throttled load. So it starts only when ALL of these resolve:
  //       · document.fonts.ready  (Nohemi swap won't reflow mid-cascade)
  //       · hero image decoded    (the big decode is off the cascade's frames)
  //       · entrance transition   (window.AL_ENTRANCE — task 1; resolved instantly if none)
  //     …with a 2.5s FAILSAFE that starts it anyway if any gate hangs. A final rAF pushes
  //     the first cascade frame off whatever frame ScrollTrigger init landed on.
  function fontsReady() {
    return (document.fonts && document.fonts.ready) ? document.fonts.ready : Promise.resolve();
  }
  function heroImageDecoded() {
    const img = document.querySelector('[data-al-hero-img] img');
    if (!img) return Promise.resolve();
    if (img.decode) return img.decode().catch(() => {});
    if (img.complete) return Promise.resolve();
    return new Promise((res) => {
      img.addEventListener('load', res, { once: true });
      img.addEventListener('error', res, { once: true });
    });
  }
  function entranceReady() {
    const e = window.AL_ENTRANCE;
    return e && typeof e.then === 'function' ? e : Promise.resolve();
  }
  function whenHeroReady(cb) {
    let done = false;
    const run = () => { if (!done) { done = true; requestAnimationFrame(cb); } };
    Promise.all([fontsReady(), heroImageDecoded(), entranceReady()]).then(run);
    setTimeout(run, 2500); // failsafe — never leave the headline hidden
  }

  const pending = [];

  // Entrance-curtain handshake: the curtain (assets/al-hero-curtain.js) holds the wipe until the
  // hero SPLIT is built (among its other gates), so init frames stay covered. We resolve
  // window.AL_SPLIT_READY once the split DOM exists. Created here if the curtain hasn't already
  // (curtain's early inline script may pre-create it); resolves instantly on pages with no headline.
  (function ensureSplitReadyPromise() {
    if (!window.AL_SPLIT_READY) {
      window.AL_SPLIT_READY = new Promise((res) => { window.__alSplitReadyResolve = res; });
    }
  })();
  function markSplitReady() {
    if (window.__alSplitReadyResolve) { window.__alSplitReadyResolve(); window.__alSplitReadyResolve = null; }
  }

  function collect(scope) {
    const r = scope || document;
    r.querySelectorAll('[data-al-hero-headline]').forEach((el) => {
      const job = build(el, el);
      if (job) pending.push(job);
    });
    r.querySelectorAll('[data-al-collection-title]').forEach((el) => {
      const job = build(el, el.querySelector('[data-al-collection-title-text]'));
      if (job) pending.push(job);
    });
    // NEUTRAL scroll-reveal alias (additive 2026-07-24) — same scroll-fired cascade as the collection
    // title, but intent-named so any page (About, future) can opt in without borrowing a "collection"
    // attribute. Text target = the [data-al-reveal-scroll-text] child if present (keeps siblings out of
    // the split, like the collection <sup>), else the element itself.
    r.querySelectorAll('[data-al-reveal-scroll]').forEach((el) => {
      const job = build(el, el.querySelector('[data-al-reveal-scroll-text]') || el);
      if (job) pending.push(job);
    });
  }

  function start(scope) {
    collect(scope);
    unarm(); // splits built + un-hidden; safe to drop the global pre-hide class
    markSplitReady(); // signal the entrance curtain: the split DOM now exists

    const jobs = pending.splice(0);
    // Collection (scroll-fired) → attach ScrollTrigger now; it waits for scroll anyway.
    jobs.filter((j) => j.onScroll).forEach(playOnScroll);
    // Hero (load-fired) → gate on fonts + image decode + entrance, off the init frame.
    const heroJobs = jobs.filter((j) => !j.onScroll);
    if (heroJobs.length) {
      whenHeroReady(() => {
        try { window.__alHeroRevealStart = performance.now(); } catch (e) {} // trace marker (harmless)
        heroJobs.forEach(playImmediate);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => start(), { once: true });
  } else {
    start();
  }

  // Theme editor: a re-rendered section arrives unsplit — re-split within it.
  document.addEventListener('shopify:section:load', (e) => start(e.target));
})();
